import {
  Box,
  Typography,
  Chip,
  styled,
  ChipProps,
  useTheme,
} from '@mui/material';
import {NoJobSaved, WhiteCrossOutlined} from '@jobseeker/lotus/assets/icons';
import {Image} from '@jobseeker/lotus/components/Image';
import {useEffect, useState} from 'react';
import TagsSkeleton from '@components/PersonalizeJobfeed/TagsSkeleton';

type Tag = {id: number; name: string; count?: number; select?: boolean};

type SelectedTagsProps = {
  selected: Array<Tag>;
  handleTagSelection: (
    name: string,
    tagId: number,
    operation: 'SELECT' | 'DESELECT'
  ) => void;
};

type CustomChipProps = ChipProps & {
  select?: boolean;
};

const CustomChip = styled(Chip)<CustomChipProps>(props => ({
  background: props?.select
    ? '#49966E'
    : 'linear-gradient(0deg, rgba(255, 255, 255, 0.70) 0%, rgba(255, 255, 255, 0.70) 100%), #CBD5E1',
  padding: `4px ${props.select ? 12 : 16}px 4px ${props.select ? 12 : 16}px`,
  width: 'fit-content',
  fontWeight: props?.select ? 500 : 400,
  color: props?.select ? '#fff' : 'primary',
  '.MuiChip-label': {
    padding: '0px',
  },
  '.MuiChip-deleteIcon': {
    marginRight: '0px',
    marginLeft: '2px',
  },
}));

const SelectedTags = ({selected, handleTagSelection}: SelectedTagsProps) => {
  const [selectedTags, setSelectedTags] = useState<Array<Tag> | null>(null);

  useEffect(() => {
    const filtered = selected.filter(tag => tag.select);
    // if (filtered.length > 0) {
    setSelectedTags(filtered);
    // }
  }, [selected]);

  const theme = useTheme();

  const TagsHeader = () => (
    <Box sx={{px: 2, display: {xxs: 'none', lg: 'block'}}}>
      <Typography
        variant="body1"
        fontWeight={selectedTags && selectedTags?.length > 0 ? 500 : 400}
        color={
          selectedTags && selectedTags?.length > 0 ? 'text' : 'text.secondary'
        }
      >
        {`Selected Tags ${
          selectedTags ? `(${selectedTags?.length ?? 0})` : ''
        }`}
      </Typography>
    </Box>
  );

  const TagsSelectionView = () => {
    if (selectedTags && selectedTags?.length > 0) {
      return selectedTags?.map((tagInfo: Tag, index: number) => (
        <Box ml={'8px'} mt={1.5} key={`${index}_${tagInfo.name}`}>
          <CustomChip
            deleteIcon={
              <Image
                src={WhiteCrossOutlined}
                alt={'Close Icon'}
                width={14}
                height={14}
                objectFit="cover"
                style={{cursor: 'pointer'}}
              />
            }
            key={`filterchip-filter`}
            label={tagInfo.name}
            onDelete={() => {
              handleTagSelection(tagInfo.name, tagInfo.id, 'DESELECT');
            }}
            select={tagInfo.select}
            sx={
              tagInfo.select
                ? {
                    ':hover': {
                      backgroundColor: theme.palette.primary.main,
                    },
                  }
                : {}
            }
          />
        </Box>
      ));
    }
    return (
      <Box
        display={'flex'}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        px={{xxs: 2, md: 3}}
      >
        <Image
          src={NoJobSaved}
          alt={'NoJobSaved'}
          width={84}
          height={84}
          object-fit="contain"
        />
        <Typography variant="subtitle3" color={'text.secondary'}>
          No tags selected
        </Typography>
      </Box>
    );
  };

  const isTagsLoading = selectedTags === null;

  return (
    <>
      <TagsHeader />
      <Box
        mt={selectedTags ? (selectedTags?.length ? 1.25 : 8.75) : 1}
        display={'flex'}
        justifyContent={
          selectedTags && selectedTags?.length > 0 ? '' : 'center'
        }
        flexWrap={'wrap'}
        px={{lg: 1}}
        mb={1}
      >
        {isTagsLoading ? (
          <Box display={'flex'} flexWrap={'wrap'} width={'100%'}>
            <TagsSkeleton skeletonChips={15} />
          </Box>
        ) : (
          <TagsSelectionView />
        )}
      </Box>
    </>
  );
};

export default SelectedTags;
